import {
  ArrowDown,
  ArrowUp,
  Boxes,
  Briefcase,
  Calendar,
  Camera,
  Coins,
  Compass,
  Gamepad2,
  GitFork,
  Globe,
  Heart,
  Link2,
  LogOut,
  MapPin,
  MessageSquare,
  Plus,
  PlusSquare,
  Search,
  Send,
  Share2,
  ShieldCheck,
  Star,
  Trophy,
  Trash2,
  Upload,
  User,
  Users,
} from "lucide-react";
import {
  FormEvent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import * as repo from "./services/repository";
import type {
  FeedItem,
  HubChain,
  HubComment,
  HubMap,
  HubProfile,
  ReactiveWorldsMapPackage,
  TargetType,
} from "./types";
import { parseMap, remixPackage, titleOf } from "./utils/mapPackage";
import {
  blankMapPackage,
  createProject,
  markProjectPublished,
  listProjects,
  loadProject,
} from "./projects/projectRepository";
import { RuntimeFrame, type RuntimeFrameHandle, type RuntimeSaveStatus } from "./runtime/RuntimeFrame";
import { loadPlayerState, loadPublicHoard, savePlayerState } from "./services/playerStateRepository";
import * as economy from "./services/economyRepository";
import { evaluateTrophies, type TrophyResult } from "./core/trophies";
import { emitUiAudio } from "./core/audioRuntime";
import { ShareDialog, type SharePayload } from "./ShareDialog";
import {
  EditProjectRoute,
  JoinedWorldRoute,
  PlayMapRoute,
  StudioRoute,
} from "./runtime/UnifiedRoutes";

const BugBoard = lazy(() => import("./BugBoard"));
function BugBoardRoute() {
  return <Suspense fallback={<main className="bug-page"><div className="bug-empty">Loading bug reports…</div></main>}><BugBoard /></Suspense>;
}

type PixelGlyphName = "brand" | "play" | "create" | "hoard" | "community" | "profile" | "help" | "bug" | "manage" | "hoardEdit" | "wardrobe";

function PixelGlyph({ name, size = 20 }: { name: PixelGlyphName; size?: number }) {
  const art = {
    brand: <path d="M2 3h2v2h2V3h4v2h2V3h2v7h-2v3H4v-3H2V3Zm4 4v2h2V7H6Zm4 0v2h2V7h-2Zm-4 4h6v2H6v-2Z" />,
    play: <path d="M4 4h8v2h2v6h-3v-2H5v2H2V6h2V4Zm1 3v1H4v1h1v1h1V9h1V8H6V7H5Zm5 0v2h2V7h-2Z" />,
    create: <path d="M3 2h3v2h1v1l5 5-2 2-5-5H4V6H2V3h1V2Zm8 8h2v2h-1v1h-2v-2h1v-1ZM2 11h5v3H2v-3Z" />,
    hoard: <path d="M3 3h10v3h1v8H2V6h1V3Zm2 2v1h6V5H5ZM4 8v4h8V8H4Zm3 1h2v2H7V9Z" />,
    community: <path d="M3 3h3v3H3V3Zm7 0h3v3h-3V3ZM6 6h4v2H6V6ZM2 9h5v4H2V9Zm7 0h5v4H9V9Zm-2 1h2v2H7v-2Z" />,
    profile: <path d="M6 2h4v2h2v4h-2v2h3v2h1v2H2v-2h1v-2h3V8H4V4h2V2Zm0 3v2h4V5H6Zm0 6v1h4v-1H6Z" />,
    help: <path d="M5 2h6v1h2v5h-2v1H9v2H7V8h2V7h2V4H5v2H3V4h2V2Zm2 11h2v2H7v-2Z" />,
    bug: <path d="M6 2h4v2h2v2h2v2h-2v1h2v2h-2v1h-2v2H6v-2H4v-1H2V9h2V8H2V6h2V4h2V2Zm0 4v5h4V6H6Zm1 1h1v1H7V7Zm2 0h1v1H9V7Z" />,
    manage: <path d="M2 3h8v1h4v2h-4V5H2V3Zm0 4h3v1h9v2H5V9H2V7Zm0 4h7v1h5v2H9v-1H2v-2Zm9-9h2v3h-2V2ZM4 6h2v5H4V6Zm4 4h2v5H8v-5Z" />,
    hoardEdit: <path d="M2 2h8v2H4v8h8V6h2v8H2V2Zm8 2h2v2h-2V4Zm2-2h2v2h-2V2ZM5 9l5-5 2 2-5 5H5V9Z" />,
    wardrobe: <path d="M4 2h8v12H4V2Zm2 2v8h2V4H6Zm3 0v8h1V4H9Zm0 4h1v1H9V8Z" />,
  }[name];
  return (
    <svg
      className={`pixel-glyph pixel-glyph-${name}`}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden="true"
      shapeRendering="crispEdges"
      fill="currentColor"
    >
      {art}
    </svg>
  );
}

function Header() {
  const { user, logout, firebaseMode, isAdmin } = useAuth();
  const location = useLocation();
  const [gold, setGold] = useState(0);
  useEffect(() => {
    if (!user) return;
    const refresh = () => void Promise.all([loadPlayerState(user.uid), economy.walletGold(user.uid)]).then(([state, wallet]) =>
        setGold(wallet ?? Math.max(0, Number(state?.gold) || 0)),
      );
    refresh();
    window.addEventListener("rw:wallet-changed", refresh);
    return () => window.removeEventListener("rw:wallet-changed", refresh);
  }, [user]);
  const nav: Array<readonly [string, string, PixelGlyphName]> = [
    ["/play", "PLAY", "play"],
    ["/build", "CREATE", "create"],
    ["/hoard", "HOARD", "hoard"],
    [user ? `/profile/${user.uid}` : "/hoard", "PROFILE", "profile"],
    ["/community", "COMMUNITY", "community"],
  ];
  const section = location.pathname.split("/")[1] || "play";
  const sectionTone = section === "build" || section === "create" || section === "publish" || section === "studio"
    ? "create"
    : section === "profile" || section === "character"
      ? "profile"
      : section === "community"
        ? "community"
        : section === "play" || section === "map" || section === "chain" || section === "world" || section === "help"
          ? "play"
          : "hoard";
  useEffect(() => {
    document.documentElement.dataset.section = sectionTone;
    return () => {
      delete document.documentElement.dataset.section;
    };
  }, [sectionTone]);
  return (
    <header className={`topbar topbar-${sectionTone}`}>
      <Link className="brand" to="/">
        <img className="brand-logo" src="/mattermaze.png" alt="MatterMaze" />
      </Link>
      <nav>
        {nav.map(([to, label, glyph]) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              isActive || (label === "PROFILE" && section === "profile")
                ? "active"
                : ""
            }
          >
            <PixelGlyph name={glyph} size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="account">
        <span className="shell-gold" title="Player gold">
          <Coins size={16} />
          <b>{gold.toLocaleString()}</b>
        </span>
        {user && (
          <Link className="account-profile" to={`/profile/${user.uid}`}>
            <PixelGlyph name="profile" size={16} />
            <span>{user.displayName}</span>
          </Link>
        )}
        <NavLink className={({ isActive }) => `tutorial-shortcut${isActive ? " active" : ""}`} to="/help" title="Controls and game guide" aria-label="Controls and game guide">
          <PixelGlyph name="help" size={17} />
        </NavLink>
        <NavLink className={({ isActive }) => `bug-shortcut${isActive ? " active" : ""}`} to="/bugs" title="Report a bug" aria-label="Bug reports">
          <PixelGlyph name="bug" size={17} />
        </NavLink>
        {isAdmin && (
          <Link className="admin-shortcut" to="/admin" title="Administration">
            <ShieldCheck size={16} />
          </Link>
        )}
        {firebaseMode && user && (
          <button onClick={() => void logout()}>
            <LogOut size={15} />
          </button>
        )}
      </div>
    </header>
  );
}
function Stats({
  likes,
  comments,
  plays,
  remixes,
  rating,
  ratings,
}: {
  likes: number;
  comments: number;
  plays: number;
  remixes?: number;
  rating?: number;
  ratings?: number;
}) {
  return (
    <div className="stats">
      <span>
        <Heart size={13} />
        {likes}
      </span>
      <span>
        <MessageSquare size={13} />
        {comments}
      </span>
      <span>
        <Gamepad2 size={13} />
        {plays}
      </span>
      {rating !== undefined && ratings !== undefined && (
        <span className="rating-stat" title={`${ratings} maze ratings`}>
          <Star size={13} fill={ratings ? "currentColor" : "none"} />
          {ratings ? rating.toFixed(1) : "NEW"}
          <small>({ratings})</small>
        </span>
      )}
      {remixes !== undefined && (
        <span>
          <GitFork size={13} />
          {remixes}
        </span>
      )}
    </div>
  );
}
function MapRating({
  mapId,
  ownerId,
  initialAverage,
  initialCount,
}: {
  mapId: string;
  ownerId: string;
  initialAverage: number;
  initialCount: number;
}) {
  const { user, isAdmin } = useAuth();
  const [userRating, setUserRating] = useState<number | null>(null),
    [average, setAverage] = useState(initialAverage),
    [count, setCount] = useState(initialCount),
    [hover, setHover] = useState<number | null>(null),
    [loading, setLoading] = useState(false),
    [error, setError] = useState("");
  const canRate = !!user && (user.uid !== ownerId || isAdmin);
  useEffect(() => {
    setAverage(initialAverage);
    setCount(initialCount);
    setUserRating(null);
    if (user) void repo.mapRating(user.uid, mapId).then(setUserRating);
  }, [mapId, user, initialAverage, initialCount]);
  const rate = async (value: number) => {
    if (!user || !canRate || loading) return;
    setLoading(true);
    setError("");
    try {
      const next = await repo.rateMap(user, mapId, value);
      setUserRating(next.userRating);
      setAverage(next.averageRating);
      setCount(next.ratingCount);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save rating.");
    } finally {
      setLoading(false);
    }
  };
  const shown = hover ?? userRating ?? Math.round(average);
  return (
    <div className="map-rating">
      <div className="map-rating-label">
        <small>COMMUNITY RATING</small>
        <b>{count ? average.toFixed(1) : "Not rated"}</b>
        <span>{count} {count === 1 ? "rating" : "ratings"}</span>
      </div>
      <div className="map-rating-stars" onMouseLeave={() => setHover(null)}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            type="button"
            key={star}
            className={star <= shown ? "filled" : ""}
            disabled={!canRate || loading}
            onMouseEnter={() => canRate && setHover(star)}
            onFocus={() => canRate && setHover(star)}
            onBlur={() => setHover(null)}
            onClick={() => void rate(star)}
            aria-label={`Rate this maze ${star} out of 5 stars`}
            title={!canRate ? "Creators cannot rate their own mazes" : `${star} stars`}
          >
            <Star size={24} fill="currentColor" />
          </button>
        ))}
      </div>
      {userRating && <small className="your-rating">Your rating: {userRating}/5</small>}
      {error && <small className="rating-error">{error}</small>}
    </div>
  );
}
function MapCard({ map }: { map: HubMap }) {
  return (
    <article className="card">
      <Link
        className={`thumb ${map.theme}`}
        style={
          map.thumbnailUrl
            ? { backgroundImage: `url(${map.thumbnailUrl})` }
            : undefined
        }
        to={`/map/${map.id}`}
      >
        {!map.thumbnailUrl && <span>{map.theme.toUpperCase()}</span>}
        {map.remixOf && (
          <em>
            <GitFork size={11} />
            REMIX
          </em>
        )}
        {map.verified && <strong className="verified-map-ribbon">✓ VERIFIED · {map.bountyGold}G</strong>}
      </Link>
      <div className="card-body">
        <small>{map.levelType} · {map.theme.toUpperCase()}</small>
        <Link className="title" to={`/map/${map.id}`}>
          {map.title}
        </Link>
        <Link className="author" to={`/profile/${map.authorId}`}>
          by {map.authorName}
        </Link>
        <p>{map.description || "No description yet."}</p>
        <Stats
          likes={map.likeCount}
          comments={map.commentCount}
          plays={map.playCount}
          remixes={map.remixCount}
          rating={map.averageRating}
          ratings={map.ratingCount}
        />
      </div>
    </article>
  );
}
function ChainCard({ chain }: { chain: HubChain }) {
  return (
    <article className="card chain">
      <div className="card-body">
        <small>JOINED MAZE · {chain.segments.length} MAZES</small>
        <Link className="title" to={`/chain/${chain.id}`}>
          {chain.title}
        </Link>
        <Link className="author" to={`/profile/${chain.authorId}`}>
          by {chain.authorName}
        </Link>
        <div className="segments">
          {chain.segments.slice(0, 5).map((s, i) => (
            <span key={`${s.mapId}-${i}`}>
              <b>{i + 1}</b>
              {s.title}
            </span>
          ))}
        </div>
        <p>{chain.description || "A connected sequence of mazes."}</p>
        <Stats
          likes={chain.likeCount}
          comments={chain.commentCount}
          plays={chain.playCount}
        />
      </div>
    </article>
  );
}
function ProfileCard({ profile }: { profile: HubProfile }) {
  return (
    <article className="card creator-card">
      <div className="card-body">
        <div className="creator-avatar">
          {profile.avatarUrl ? (
            <img src={profile.avatarUrl} alt="" />
          ) : (
            <User size={24} />
          )}
        </div>
        <small>CREATOR · {profile.followerCount} FOLLOWERS</small>
        <Link className="title" to={`/profile/${profile.id}`}>
          {profile.displayName}
        </Link>
        <p>{profile.bio || "Maze creator."}</p>
        <Link className="author" to={`/profile/${profile.id}`}>
          View creator profile
        </Link>
      </div>
    </article>
  );
}
function CommunityCreatorCard({ profile, mazeCount }: { profile: HubProfile; mazeCount: number }) {
  return (
    <article className="community-creator-card">
      <Link className="community-creator-avatar" to={`/profile/${profile.id}`} aria-label={`View ${profile.displayName}'s creator page`}>
        {profile.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <PixelGlyph name="profile" size={34} />}
      </Link>
      <div className="community-creator-copy">
        <small>CREATOR</small>
        <Link className="title" to={`/profile/${profile.id}`}>{profile.displayName}</Link>
        <p>{profile.bio || "Building new MatterMaze challenges."}</p>
        <div className="community-creator-stats">
          <span><b>{mazeCount}</b> {mazeCount === 1 ? "MAZE" : "MAZES"}</span>
          <span><b>{profile.followerCount}</b> FOLLOWERS</span>
        </div>
      </div>
      <div className="community-creator-actions">
        <Link to={`/profile/${profile.id}`}><PixelGlyph name="profile" size={15} /> CREATOR PAGE</Link>
        <Link to={`/hoard/${profile.id}`}><PixelGlyph name="hoard" size={15} /> VISIT HOARD</Link>
      </div>
    </article>
  );
}
function Social({
  type,
  id,
  initialLikes,
}: {
  type: TargetType;
  id: string;
  initialLikes: number;
}) {
  const { user } = useAuth();
  const [isLiked, setLiked] = useState(false),
    [likes, setLikes] = useState(initialLikes),
    [list, setList] = useState<HubComment[]>([]),
    [text, setText] = useState("");
  useEffect(() => {
    void repo.comments(type, id).then(setList);
    if (user) void repo.liked(user.uid, type, id).then(setLiked);
  }, [type, id, user]);
  const like = async () => {
    if (!user) return;
    const yes = await repo.toggleLike(user, type, id);
    setLiked(yes);
    setLikes((v) => Math.max(0, v + (yes ? 1 : -1)));
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !text.trim()) return;
    const c = await repo.addComment(user, type, id, text);
    setList((v) => [...v, c]);
    setText("");
  };
  return (
    <section className="social">
      <div className="social-head">
        <button className={isLiked ? "liked" : ""} onClick={() => void like()}>
          <Heart size={17} fill={isLiked ? "currentColor" : "none"} />
          {likes} likes
        </button>
        <span>{list.length} comments</span>
      </div>
      {list.map((c) => (
        <div className="comment" key={c.id}>
          <Link to={`/profile/${c.userId}`}>
            <b>{c.userName}</b>
          </Link>
          <p>{c.content}</p>
          <small>{c.createdAt.toLocaleString()}</small>
        </div>
      ))}
      {!list.length && <div className="empty-inline">No comments yet.</div>}
      <form className="comment-form" onSubmit={submit}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment…"
          maxLength={500}
        />
        <button disabled={!text.trim()}>
          <Send size={15} />
        </button>
      </form>
    </section>
  );
}
function Feed() {
  const { user } = useAuth();
  const [items, setItems] = useState<FeedItem[]>([]),
    [loading, setLoading] = useState(true),
    [profiles, setProfiles] = useState<HubProfile[]>([]),
    [maps, setMaps] = useState<HubMap[]>([]),
    [query, setQuery] = useState(""),
    [tab, setTab] = useState<"feed" | "following" | "trending" | "creators" | "mine">(
      "feed",
    ),
    [followIds, setFollowIds] = useState<string[]>([]);
  useEffect(() => {
    void Promise.all([
      repo.feed(),
      user ? repo.following(user.uid) : Promise.resolve([]),
      repo.profiles(),
      repo.maps({ publicOnly: true }),
    ])
      .then(([feed, following, creatorProfiles, publicMaps]) => {
        setItems(feed);
        setFollowIds(following.map((p) => p.id));
        setProfiles(creatorProfiles);
        setMaps(publicMaps);
      })
      .finally(() => setLoading(false));
  }, [user]);
  const visible =
    tab === "following"
      ? items.filter((x) => followIds.includes(x.data.authorId))
      : tab === "mine"
        ? items.filter((x) => x.data.authorId === user?.uid)
        : tab === "trending"
          ? [...items].sort(
              (a, b) =>
                b.data.likeCount +
                b.data.playCount +
                (b.type === "map"
                  ? b.data.averageRating * 20 + b.data.ratingCount * 3
                  : 0) -
                (a.data.likeCount +
                  a.data.playCount +
                  (a.type === "map"
                    ? a.data.averageRating * 20 + a.data.ratingCount * 3
                    : 0)),
            )
          : items;
  const term = query.trim().toLowerCase();
  const filtered = term
    ? visible.filter((x) =>
        [x.data.title, x.data.authorName, x.data.description]
          .join(" ")
          .toLowerCase()
          .includes(term),
      )
    : visible;
  const filteredCreators = [...profiles]
    .filter((profile) =>
      !term || [profile.displayName, profile.bio, profile.location, profile.occupation]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term),
    )
    .sort((a, b) => b.followerCount - a.followerCount || a.displayName.localeCompare(b.displayName));
  const leadingCreators = [...profiles]
    .sort((a, b) => b.followerCount - a.followerCount)
    .slice(0, 5);
  const popularMaps = [...maps]
    .sort(
      (a, b) =>
        b.averageRating - a.averageRating ||
        b.ratingCount - a.ratingCount ||
        b.playCount - a.playCount,
    )
    .slice(0, 4);
  return (
    <main className="page community-page">
      <section className="community-head">
        <div>
          <small>PEOPLE · PUBLICATIONS · CONVERSATIONS</small>
          <h1>Community</h1>
          <p>Follow creators, discuss their work and discover the community’s newest mazes.</p>
        </div>
        <Link className="primary" to="/publish">
          <PlusSquare size={17} />
          PUBLISH MAZE
        </Link>
      </section>
      <section className="community-browser-bar">
        <label className="search community-search"><Search size={15} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tab === "creators" ? "Search creators…" : "Search publications and creators…"} /></label>
        <div className="community-tabs" role="tablist" aria-label="Community views">
          <button className={tab === "feed" ? "active" : ""} onClick={() => setTab("feed")}>FEED</button>
          <button className={tab === "following" ? "active" : ""} onClick={() => setTab("following")}>FOLLOWING</button>
          <button className={tab === "trending" ? "active" : ""} onClick={() => setTab("trending")}>TRENDING</button>
          <button className={tab === "creators" ? "active" : ""} onClick={() => setTab("creators")}>CREATORS</button>
          <button className={tab === "mine" ? "active" : ""} onClick={() => setTab("mine")}>MINE</button>
        </div>
      </section>
      <div className="community-layout">
        <section className="community-stream">
          {loading ? <Empty text="Loading the community…" /> : tab === "creators" ? (
            filteredCreators.length ? <div className="community-creator-grid">
              {filteredCreators.map((profile) => <CommunityCreatorCard key={profile.id} profile={profile} mazeCount={maps.filter((map) => map.authorId === profile.id).length} />)}
            </div> : <Empty text="No creators match your search." />
          ) : filtered.length ? (
            <div className="community-feed-grid">
              {filtered.map((x) => x.type === "map" ? <MapCard key={`m${x.id}`} map={x.data} /> : <ChainCard key={`c${x.id}`} chain={x.data} />)}
            </div>
          ) : <Empty text="No publications match this view." />}
        </section>
        <aside className="community-ranking">
          <section className="pixel-panel">
            <small>TRENDING CREATORS</small>
            <ol>{leadingCreators.map((p) => <li key={p.id}><b>{p.displayName}</b><span>{p.followerCount} followers</span><Link to={`/profile/${p.id}`}>VIEW</Link></li>)}</ol>
            {!leadingCreators.length && <p>No creators yet.</p>}
          </section>
          <section className="pixel-panel popular-list">
            <small>TOP RATED MAZES</small>
            {popularMaps.map((m) => <Link key={m.id} to={`/map/${m.id}`}><span className={`mini-map ${m.theme}`} style={m.thumbnailUrl ? { backgroundImage: `url(${m.thumbnailUrl})` } : undefined} /><b>{m.title}</b><em><Star size={10} fill="currentColor" /> {m.ratingCount ? m.averageRating.toFixed(1) : "NEW"}</em></Link>)}
          </section>
        </aside>
      </div>
    </main>
  );
}
function Heading({ title, link }: { title: string; link?: string }) {
  return (
    <div className="heading">
      <h2>{title}</h2>
      {link && <Link to={link}>Browse all</Link>}
    </div>
  );
}
function Empty({ text }: { text: string }) {
  return <div className="empty">{text}</div>;
}
function Discover() {
  const [allMaps, setMaps] = useState<HubMap[]>([]),
    [allChains, setChains] = useState<HubChain[]>([]),
    [allProfiles, setProfiles] = useState<HubProfile[]>([]),
    [q, setQ] = useState(""),
    [tab, setTab] = useState<"maps" | "chains" | "creators">("maps");
  useEffect(() => {
    void Promise.all([
      repo.maps({ publicOnly: true }),
      repo.chains({ publicOnly: true }),
      repo.profiles(),
    ]).then(([m, c, p]) => {
      setMaps(m);
      setChains(c);
      setProfiles(p);
    });
  }, []);
  const s = q.toLowerCase(),
    m = allMaps.filter((x) =>
      [x.title, x.authorName, x.description, x.theme, ...x.tags]
        .join(" ")
        .toLowerCase()
        .includes(s),
    ),
    c = allChains.filter((x) =>
      [x.title, x.authorName, x.description, ...x.segments.map((v) => v.title)]
        .join(" ")
        .toLowerCase()
        .includes(s),
    ),
    p = allProfiles.filter((x) =>
      [x.displayName, x.bio].join(" ").toLowerCase().includes(s),
    );
  return (
    <main className="page">
      <div className="page-title">
        <div>
          <small>DISCOVER</small>
          <h1>Find mazes and their creators.</h1>
        </div>
        <label className="search">
          <Search size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search mazes, creators, themes…"
          />
        </label>
      </div>
      <div className="tabs">
        <button
          className={tab === "maps" ? "active" : ""}
          onClick={() => setTab("maps")}
        >
          Mazes · {m.length}
        </button>
        <button
          className={tab === "chains" ? "active" : ""}
          onClick={() => setTab("chains")}
        >
          Joined mazes · {c.length}
        </button>
        <button
          className={tab === "creators" ? "active" : ""}
          onClick={() => setTab("creators")}
        >
          Creators · {p.length}
        </button>
      </div>
      <div className="grid">
        {tab === "maps"
          ? m.map((x) => <MapCard key={x.id} map={x} />)
          : tab === "chains"
            ? c.map((x) => <ChainCard key={x.id} chain={x} />)
            : p.map((x) => <ProfileCard key={x.id} profile={x} />)}
      </div>
    </main>
  );
}
function Publish() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const [pkg, setPkg] = useState<ReactiveWorldsMapPackage | null>(null),
    [sourceProject, setSourceProject] = useState<Awaited<ReturnType<typeof loadProject>>>(null),
    [title, setTitle] = useState(""),
    [description, setDescription] = useState(""),
    [tags, setTags] = useState(""),
    [publicMap, setPublic] = useState(true),
    [error, setError] = useState(""),
    [saving, setSaving] = useState(false);
  useEffect(() => {
    const id = search.get("project");
    if (!id) return;
    void loadProject(id).then((p) => {
      if (p) {
        setSourceProject(p);
        setPkg(p.package);
        setTitle(titleOf(p.package));
      }
    });
  }, [search]);
  const file = async (f?: File) => {
    if (!f) return;
    try {
      const p = parseMap(await f.text());
      setPkg(p);
      setSourceProject(null);
      setTitle(titleOf(p));
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid maze");
    }
  };
  const openEditor = async () => {
    if (!pkg) return;
    const p = await createProject(pkg);
    nav(`/build/${p.id}`);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !pkg) return;
    if (!sourceProject?.verification) {
      emitUiAudio("ui.verify_fail");
      setError("Complete this exact maze revision in Build before publishing.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const id = await repo.publishMap(user, pkg, {
        title,
        description,
        isPublic: publicMap,
        verification: sourceProject.verification,
        tags: tags
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      });
      const projectId = search.get("project");
      if (projectId) await markProjectPublished(projectId, id);
      emitUiAudio("ui.publish");
      nav(`/map/${encodeURIComponent(id)}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Publish failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <main className="page narrow">
      <div className="page-title">
        <div>
          <small>PUBLISH</small>
          <h1>Publish a verified maze.</h1>
          <p>Complete the current revision yourself, then share it with the community.</p>
        </div>
      </div>
      {!pkg ? (
        <label className="drop">
          <PlusSquare size={34} />
          <b>Choose an exported MatterMaze file</b>
          <span>Or create or remix a maze in Build.</span>
          <input
            type="file"
            accept="application/json,.json"
            onChange={(e) => void file(e.target.files?.[0])}
          />
        </label>
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="package">
            <div>
              <b>{titleOf(pkg)}</b>
              <span>
                {pkg.theme} · {String(pkg.ugc.levelType || "Puzzle")} · physics{" "}
                {pkg.engineVersion || pkg.version} · {pkg.cols}×{pkg.rows}
              </span>
              {pkg.hub?.remixOf && <span>Remix of {pkg.hub.remixOf}</span>}
              <span className={sourceProject?.verification ? "publish-verified" : "publish-unverified"}>
                {sourceProject?.verification ? "✓ Creator-completed revision" : "○ Verification required · open this maze in Build and complete it"}
              </span>
            </div>
            <button
              type="button"
              className="secondary"
              onClick={() => void openEditor()}
            >
              <Gamepad2 size={15} />
              Open editor
            </button>
          </div>
          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            Description
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label>
            Tags
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="lava, machinery, water"
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={publicMap}
              onChange={(e) => setPublic(e.target.checked)}
            />
            Public in feed and discovery
          </label>
          <div className="actions">
            <button
              type="button"
              className="secondary"
              onClick={() => setPkg(null)}
            >
              Choose another
            </button>
            <button className="primary" disabled={saving || !sourceProject?.verification}>
              {saving ? "Preparing preview + publishing…" : "Publish maze"}
            </button>
          </div>
        </form>
      )}
      {error && <div className="error">{error}</div>}
    </main>
  );
}
function MapDetail() {
  const { mapId = "" } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [m, setMap] = useState<HubMap | null>(null),
    [pkg, setPkg] = useState<ReactiveWorldsMapPackage | null>(null),
    [lineageMaps, setLineageMaps] = useState<HubMap[]>([]),
    [saved, setSaved] = useState(false),
    [saveCount, setSaveCount] = useState(0),
    [share, setShare] = useState<SharePayload | null>(null),
    [error, setError] = useState(""),
    [removing, setRemoving] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setError("");
      setMap(null);
      setPkg(null);
      let x: HubMap | null = null;
      for (let attempt = 0; attempt < 4 && !x; attempt++) {
        x = await repo.map(mapId);
        if (!x && attempt < 3)
          await new Promise((resolve) =>
            window.setTimeout(resolve, 250 * (attempt + 1)),
          );
      }
      if (!x)
        throw new Error(
          "The published maze could not be loaded. Return to the feed and try opening it again.",
        );
      const p = await repo.mapPackage(x);
      if (!cancelled) {
        setMap(x);
        setPkg(p);
        setSaveCount(x.saveCount);
        const ancestors = await Promise.all((x.lineage || []).map((id) => repo.map(id)));
        if (!cancelled) setLineageMaps(ancestors.filter((item): item is HubMap => !!item));
        if (user && !cancelled) setSaved(await economy.isMapSaved(x.id, user.uid));
      }
    };
    void load().catch((e) => {
      if (!cancelled) setError(e instanceof Error ? e.message : "Load failed");
    });
    return () => {
      cancelled = true;
    };
  }, [mapId, user]);
  if (error)
    return (
      <main className="page">
        <div className="error">{error}</div>
      </main>
    );
  if (!m || !pkg)
    return (
      <main className="page">
        <Empty text="Loading maze…" />
      </main>
    );
  const remix = async () => {
    const p = await createProject(remixPackage(pkg, m), {
      sourceMapId: m.id,
      remixOf: m.id,
    });
    nav(`/build/${p.id}`);
  };
  const remove = async () => {
    if (
      !user ||
      m.authorId !== user.uid ||
      !window.confirm(
        "Remove this maze from community listings? Its original data will remain stored for joined mazes and remix lineage.",
      )
    )
      return;
    setRemoving(true);
    setError("");
    try {
      await repo.removeMap(user, m.id);
      nav(`/profile/${user.uid}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove maze");
      setRemoving(false);
    }
  };
  const toggleSave = async () => {
    try {
      const result = await economy.toggleMapSave(m.id);
      setSaved(result.saved);
      setSaveCount(result.saveCount);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save this maze");
    }
  };
  const clearRate = m.uniqueAttemptCount ? m.escapeCount / m.uniqueAttemptCount * 100 : 0;
  return (
    <main className="page">
      {m.isRemoved && (
        <div className="lineage">
          This post has been removed from community listings. Its original maze
          data and lineage are still preserved.
        </div>
      )}
      <section className="detail">
        <div
          className={`preview ${m.theme}`}
          style={
            m.thumbnailUrl
              ? { backgroundImage: `url(${m.thumbnailUrl})` }
              : undefined
          }
        />
        <div>
          <small>
            {m.levelType} · {m.theme.toUpperCase()}
          </small>
          <h1>{m.title}</h1>
          <Link className="author" to={`/profile/${m.authorId}`}>
            by {m.authorName}
          </Link>
          <p>{m.description || "No description provided."}</p>
          <div className="tagrow">
            {m.tags.map((t) => (
              <span key={t}>#{t}</span>
            ))}
          </div>
          <div className="actions left">
            {!m.isRemoved && <>
              <button className="primary" onClick={() => nav(`/play/${m.id}`)}>
                <Gamepad2 size={17} />
                Play maze
              </button>
              <button className="secondary" onClick={() => void remix()}>
                <GitFork size={17} />
                Remix in editor
              </button>
              <button className={saved ? "secondary saved-map" : "secondary"} onClick={() => void toggleSave()}>
                <Heart size={17} fill={saved ? "currentColor" : "none"} />
                {saved ? "Saved" : "Save maze"}
              </button>
              <button className="secondary" onClick={() => setShare({
                title: m.title,
                text: `Play ${m.title} by ${m.authorName} in MatterMaze. Bounty: ${m.bountyGold}G.`,
                url: `${location.origin}/map/${encodeURIComponent(m.id)}`,
                image: m.thumbnailUrl,
                kicker: m.verified ? `Verified maze · ${m.bountyGold}G bounty` : "Community maze",
                filename: `mattermaze-${m.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "maze"}.png`,
                source: "map",
              })}>
                <Share2 size={17} /> Share maze
              </button>
            </>}
            {user?.uid === m.authorId && !m.isRemoved && (
              <button
                className="secondary danger-button"
                disabled={removing}
                onClick={() => void remove()}
              >
                <Trash2 size={17} />
                {removing ? "Removing…" : "Remove post"}
              </button>
            )}
          </div>
          {!m.isRemoved && <MapRating
              mapId={m.id}
              ownerId={m.authorId}
              initialAverage={m.averageRating}
              initialCount={m.ratingCount}
            />}
          <section className="challenge-economy-panel">
            <header><span className="verified-badge">{m.verified ? "✓ VERIFIED REVISION" : "UNVERIFIED · NO BOUNTY"}</span><strong><Coins size={19} /> BOUNTY {m.bountyGold}G</strong></header>
            <div className="economy-stat-grid">
              <span><b>{m.uniqueAttemptCount.toLocaleString()}</b> ATTEMPTED</span>
              <span><b>{m.escapeCount.toLocaleString()}</b> ESCAPED</span>
              <span><b>{m.uniqueAttemptCount ? `${clearRate.toFixed(1)}%` : "—"}</b> CLEAR RATE</span>
              <span><b>{m.rematchCount.toLocaleString()}</b> REMATCHES</span>
              <span><b>{saveCount.toLocaleString()}</b> SAVES</span>
              <span><b>{m.creatorEarningsGold.toLocaleString()}G</b> CREATOR EARNINGS</span>
            </div>
            <p>Each player can claim this revision’s bounty once. Unique failures raise it; meaningful engagement funds the creator and the permanent remix lineage.</p>
          </section>
        </div>
      </section>
      {(lineageMaps.length > 0 || m.remixOf) && <section className="lineage lineage-chain"><small>PERMANENT REMIX LINEAGE · 50% ROYALTY POOL</small><div>{lineageMaps.map((ancestor) => <Link key={ancestor.id} to={`/map/${ancestor.id}`}>{ancestor.title}</Link>)}<span>{m.title}</span></div><p>The current creator keeps 50% of remix earnings. The remaining half is divided equally across every ancestor shown above.</p></section>}
      {!m.isRemoved && <Social type="map" id={m.id} initialLikes={m.likeCount} />}
      <ShareDialog payload={share} onClose={() => setShare(null)} />
    </main>
  );
}
function Join() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [available, setAvailable] = useState<HubMap[]>([]),
    [selected, setSelected] = useState<HubMap[]>([]),
    [q, setQ] = useState(""),
    [title, setTitle] = useState(""),
    [description, setDescription] = useState(""),
    [isPublic, setPublic] = useState(true);
  useEffect(() => {
    void repo.maps({ publicOnly: true }).then(setAvailable);
  }, []);
  const shown = useMemo(
    () =>
      available.filter(
        (m) =>
          !selected.some((s) => s.id === m.id) &&
          [m.title, m.authorName, m.theme]
            .join(" ")
            .toLowerCase()
            .includes(q.toLowerCase()),
      ),
    [available, selected, q],
  );
  const move = (i: number, d: number) =>
    setSelected((v) => {
      const n = [...v],
        j = i + d;
      if (j < 0 || j >= n.length) return n;
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || selected.length < 2 || !title.trim()) return;
    const id = await repo.createChain(user, {
      title,
      description,
      isPublic,
      segments: selected.map((m) => ({
        mapId: m.id,
        title: m.title,
        authorId: m.authorId,
        authorName: m.authorName,
        thumbnailUrl: m.thumbnailUrl,
        transition: "exit-to-next",
      })),
    });
    nav(`/chain/${id}`);
  };
  return (
    <main className="page">
      <div className="page-title">
        <div>
          <small>MAZE JOINER</small>
          <h1>Connect mazes into one continuous run.</h1>
          <p>Choose the order players will tackle each maze.</p>
        </div>
      </div>
      <div className="join">
        <section>
          <h2>Public mazes</h2>
          <input
            className="filter"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter mazes…"
          />
          {shown.map((m) => (
            <button
              className="pick"
              key={m.id}
              onClick={() => setSelected((v) => [...v, m])}
            >
              <Plus size={14} />
              <span>
                <b>{m.title}</b>
                <small>
                  {m.authorName} · {m.theme}
                </small>
              </span>
            </button>
          ))}
        </section>
        <form className="form" onSubmit={submit}>
          <h2>Maze sequence</h2>
          {selected.map((m, i) => (
            <div className="seq" key={`${m.id}-${i}`}>
              <b>{i + 1}</b>
              <span>
                {m.title}
                <small>Exit → next maze</small>
              </span>
              <button type="button" onClick={() => move(i, -1)}>
                <ArrowUp size={13} />
              </button>
              <button type="button" onClick={() => move(i, 1)}>
                <ArrowDown size={13} />
              </button>
              <button
                type="button"
                onClick={() => setSelected((v) => v.filter((_, j) => j !== i))}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          {!selected.length && (
            <div className="empty-inline">Add at least two mazes.</div>
          )}
          <label>
            Joined maze title
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            Description
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setPublic(e.target.checked)}
            />
            Public joined maze
          </label>
          <button
            className="primary"
            disabled={selected.length < 2 || !title.trim()}
          >
            <Link2 size={16} />
            Publish joined maze
          </button>
        </form>
      </div>
    </main>
  );
}
function ChainDetail() {
  const { chainId = "" } = useParams();
  const nav = useNavigate();
  const [c, setChain] = useState<HubChain | null>(null);
  useEffect(() => {
    void repo.chain(chainId).then(setChain);
  }, [chainId]);
  if (!c)
    return (
      <main className="page">
        <Empty text="Loading joined maze…" />
      </main>
    );
  return (
    <main className="page">
      <section className="chain-hero">
        <small>JOINED MAZE · {c.segments.length} MAZES</small>
        <h1>{c.title}</h1>
        <Link className="author" to={`/profile/${c.authorId}`}>
          by {c.authorName}
        </Link>
        <p>{c.description}</p>
        <button className="primary" onClick={() => nav(`/world/${c.id}`)}>
          <Gamepad2 size={17} />
          Start joined maze
        </button>
      </section>
      <div className="sequence">
        {c.segments.map((s, i) => (
          <Link key={`${s.mapId}-${i}`} to={`/map/${s.mapId}`}>
            <b>{i + 1}</b>
            <span>{s.title}</span>
            <small>{s.authorName}</small>
          </Link>
        ))}
      </div>
      <Social type="chain" id={c.id} initialLikes={c.likeCount} />
    </main>
  );
}
async function squareAvatarBlob(file: File) {
  if (!file.type.startsWith("image/"))
    throw new Error("Choose an image file for your profile photo.");
  if (file.size > 8 * 1024 * 1024)
    throw new Error("Profile photos must be smaller than 8 MB.");
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const next = new Image();
      next.onload = () => resolve(next);
      next.onerror = () => reject(new Error("The selected image could not be opened."));
      next.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Profile photo processing is unavailable.");
    const crop = Math.min(image.naturalWidth, image.naturalHeight),
      sourceX = (image.naturalWidth - crop) / 2,
      sourceY = (image.naturalHeight - crop) / 2;
    context.drawImage(image, sourceX, sourceY, crop, crop, 0, 0, 256, 256);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("Profile photo processing failed.")),
        "image/png",
      ),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

function Profile() {
  const { userId = "" } = useParams(),
    { user, setLocalName, isAdmin } = useAuth(),
    [p, setP] = useState<HubProfile | null>(null),
    [m, setM] = useState<HubMap[]>([]),
    [c, setC] = useState<HubChain[]>([]),
    [followed, setFollowed] = useState(false),
    [following, setFollowing] = useState<HubProfile[]>([]),
    [name, setName] = useState(""),
    [bio, setBio] = useState(""),
    [location, setLocation] = useState(""),
    [website, setWebsite] = useState(""),
    [occupation, setOccupation] = useState(""),
    [tab, setTab] = useState<"maps" | "worlds" | "about" | "following">("maps"),
    [editing, setEditing] = useState(false),
    [profileSaving, setProfileSaving] = useState(false),
    [profileError, setProfileError] = useState(""),
    [avatarSaving, setAvatarSaving] = useState(false),
    [avatarError, setAvatarError] = useState(""),
    [portraitRuntime, setPortraitRuntime] = useState(false);
  const portraitApi = useRef<RuntimeFrameHandle | null>(null),
    portraitInFlight = useRef(false),
    portraitPackage = useMemo(() => blankMapPackage(), []);
  const own = user?.uid === userId;
  useEffect(() => {
    if (user && own) void repo.ensureProfile(user);
    void Promise.all([
      repo.profile(userId),
      repo.maps({ authorId: userId }),
      repo.chains({ authorId: userId }),
      repo.following(userId),
      user && !own
        ? repo.isFollowing(user.uid, userId)
        : Promise.resolve(false),
    ]).then(([a, b, d, f, isFollowed]) => {
      setP(a);
      setM(b);
      setC(d);
      setFollowing(f);
      setFollowed(isFollowed);
      setName(a?.displayName || user?.displayName || "Builder");
      setBio(a?.bio || "");
      setLocation(a?.location || "");
      setWebsite(a?.website || "");
      setOccupation(a?.occupation || "");
    });
  }, [userId, user, own]);
  const persistAvatar = useCallback(
    async (blob: Blob) => {
      if (!user || !own) return;
      setAvatarSaving(true);
      setAvatarError("");
      try {
        const avatarUrl = await repo.uploadProfileAvatar(user, blob);
        await repo.saveProfile(user, {
          displayName: name,
          bio,
          avatarUrl,
          location,
          website,
          occupation,
        });
        setP((value) => value ? { ...value, avatarUrl, updatedAt: new Date() } : value);
      } catch (error) {
        setAvatarError(error instanceof Error ? error.message : "Profile photo save failed.");
      } finally {
        setAvatarSaving(false);
      }
    },
    [bio, location, name, occupation, own, user, website],
  );
  const uploadAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      await persistAvatar(await squareAvatarBlob(file));
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : "Profile photo processing failed.");
      setAvatarSaving(false);
    }
  };
  const bindPortraitApi = useCallback((api: RuntimeFrameHandle | null) => {
    portraitApi.current = api;
  }, []);
  const capturePortrait = useCallback(() => {
    const api = portraitApi.current;
    if (!api || portraitInFlight.current) return;
    portraitInFlight.current = true;
    void api.requestAvatar()
      .then((dataUrl) => fetch(dataUrl))
      .then((response) => response.blob())
      .then(persistAvatar)
      .catch((error) => setAvatarError(error instanceof Error ? error.message : "Character portrait failed."))
      .finally(() => {
        portraitInFlight.current = false;
        setPortraitRuntime(false);
        setAvatarSaving(false);
      });
  }, [persistAvatar]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !own || profileSaving) return;
    const displayName = name.trim() || "Builder",
      nextBio = bio.trim(),
      nextLocation = location.trim(),
      nextWebsite = website.trim(),
      nextOccupation = occupation.trim();
    setProfileSaving(true);
    setProfileError("");
    try {
      await repo.saveProfile(user, {
        displayName,
        bio: nextBio,
        location: nextLocation,
        website: nextWebsite,
        occupation: nextOccupation,
      });
      await setLocalName(displayName);
      const persisted = await repo.profile(user.uid);
      const nextProfile = persisted || (p ? {
        ...p,
        displayName,
        bio: nextBio,
        location: nextLocation,
        website: nextWebsite,
        occupation: nextOccupation,
        updatedAt: new Date(),
      } : null);
      setP(nextProfile);
      setName(nextProfile?.displayName || displayName);
      setBio(nextProfile?.bio || nextBio);
      setLocation(nextProfile?.location || nextLocation);
      setWebsite(nextProfile?.website || nextWebsite);
      setOccupation(nextProfile?.occupation || nextOccupation);
      setEditing(false);
      emitUiAudio("ui.confirm");
    } catch (error) {
      setProfileError(error instanceof Error ? error.message : "Profile save failed.");
    } finally {
      setProfileSaving(false);
    }
  };
  const toggleFollow = async () => {
    if (!user || own) return;
    const next = await repo.toggleFollowing(user, userId);
    setFollowed(next);
    setP((v) =>
      v
        ? {
            ...v,
            followerCount: Math.max(0, v.followerCount + (next ? 1 : -1)),
          }
        : v,
    );
  };
  return (
    <main className="page profile-page">
      <section className="profile detailed profile-compact">
        <div className={`profile-identity${own ? " has-avatar-actions" : ""}`}>
          <div className="avatar">
            {p?.avatarUrl ? <img src={p.avatarUrl} alt={`${p.displayName} profile`} /> : <User size={38} />}
          </div>
          {own && (
            <div className="profile-avatar-actions">
              <label className="avatar-control secondary" aria-label="Upload profile photo" title="Upload profile photo">
                <Upload size={19} />
                <input type="file" accept="image/png,image/jpeg,image/webp" disabled={avatarSaving} onChange={(event) => void uploadAvatar(event)} />
              </label>
              <button
                type="button"
                className="avatar-control secondary"
                aria-label="Take character snapshot"
                title="Take character snapshot"
                disabled={avatarSaving}
                onClick={() => {
                  setAvatarError("");
                  setAvatarSaving(true);
                  setPortraitRuntime(true);
                }}
              ><Camera size={19} /></button>
            </div>
          )}
          <div className="profile-identity-copy">
            <small>CREATOR PAGE</small>
            <h1>{p?.displayName || "Builder"}</h1>
            <p>{p?.bio || "No bio yet."}</p>
          </div>
        </div>
        <div className="profile-main">
          <div className="profile-title-row profile-primary-actions">
            {own && (
              <button className="secondary" onClick={() => { setTab("about"); if (editing) emitUiAudio("ui.cancel"); setEditing((v) => !v); }}>
                {editing ? "Close editor" : "Edit profile"}
              </button>
            )}
            <Link className="primary visit-hoard" to={own ? "/hoard" : `/hoard/${userId}`}>
              <Boxes size={16} /> {own ? "Enter Hoard" : "Visit Hoard"}
            </Link>
            {!own && user && (
              <button
                className={followed ? "secondary following" : "primary"}
                onClick={() => void toggleFollow()}
              >
                <Star size={15} fill={followed ? "currentColor" : "none"} />
                {followed ? "Following" : "Follow"}
              </button>
            )}
          </div>
          {avatarSaving && <small className="avatar-status">PREPARING PHOTO...</small>}
          {avatarError && <span className="avatar-error">{avatarError}</span>}
          <div className="profile-details">
            {own && isAdmin && (
              <Link to="/admin">
                <ShieldCheck size={14} /> Admin
              </Link>
            )}
            {p?.location && (
              <span>
                <MapPin size={14} />
                {p.location}
              </span>
            )}
            {p?.occupation && (
              <span>
                <Briefcase size={14} />
                {p.occupation}
              </span>
            )}
            {p?.website && (
              <a href={p.website} target="_blank" rel="noreferrer">
                <Globe size={14} />
                {p.website}
              </a>
            )}
            {p && (
              <span>
                <Calendar size={14} />
                Joined {p.createdAt.toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="profile-stats">
            <span>
              <b>{m.length}</b> mazes
            </span>
            <span>
              <b>{c.length}</b> joined mazes
            </span>
            <span>
              <b>
                {m.reduce((n, x) => n + x.likeCount, 0) +
                  c.reduce((n, x) => n + x.likeCount, 0)}
              </b>{" "}
              likes
            </span>
            <span>
              <b>{p?.followerCount || 0}</b> followers
            </span>
            <span>
              <b>{following.length}</b> following
            </span>
          </div>
        </div>
      </section>
      <div className="profile-tabs" role="tablist" aria-label="Creator page sections">
        <button className={tab === "maps" ? "active" : ""} onClick={() => setTab("maps")}>Mazes <b>{m.length}</b></button>
        <button className={tab === "worlds" ? "active" : ""} onClick={() => setTab("worlds")}>Joined Mazes <b>{c.length}</b></button>
        <button className={tab === "about" ? "active" : ""} onClick={() => setTab("about")}>About</button>
        <button className={tab === "following" ? "active" : ""} onClick={() => setTab("following")}>Following <b>{following.length}</b></button>
      </div>
      {tab === "following" && following.length > 0 && (
        <section className="following-strip">
          <h3>
            <Users size={16} />
            Following
          </h3>
          <div>
            {following.slice(0, 8).map((x) => (
              <Link key={x.id} to={`/profile/${x.id}`}>
                {x.avatarUrl ? (
                  <img src={x.avatarUrl} alt="" />
                ) : (
                  <User size={14} />
                )}
                <span>{x.displayName}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
      {tab === "following" && !following.length && <Empty text="This creator is not following anyone yet." />}
      {tab === "about" && !editing && (
        <section className="profile-about pixel-panel">
          <small>ABOUT THIS CREATOR</small>
          <p>{p?.bio || "No creator biography has been added yet."}</p>
          <div className="profile-details">
            {p?.location && <span><MapPin size={14} />{p.location}</span>}
            {p?.occupation && <span><Briefcase size={14} />{p.occupation}</span>}
            {p?.website && <a href={p.website} target="_blank" rel="noreferrer"><Globe size={14} />{p.website}</a>}
            {p && <span><Calendar size={14} />Joined {p.createdAt.toLocaleDateString()}</span>}
          </div>
        </section>
      )}
      {own && editing && tab === "about" && (
        <form className="form profile-edit" onSubmit={save}>
          <label>
            Display name
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Bio
            <textarea
              rows={3}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
            />
          </label>
          <div className="form-grid">
            <label>
              Location
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </label>
            <label>
              Occupation
              <input
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
              />
            </label>
          </div>
          <label>
            Website
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>
          {profileError && <div className="error">{profileError}</div>}
          <button className="primary" type="submit" disabled={profileSaving}>{profileSaving ? "Saving…" : "Save profile"}</button>
        </form>
      )}
      {tab === "maps" && (
        <>
          <Heading title="Published mazes" />
          <div className="grid profile-map-grid">
            {m.map((x) => <MapCard key={x.id} map={x} />)}
          </div>
          {!m.length && <Empty text="No published mazes yet." />}
        </>
      )}
      {tab === "worlds" && (
        <>
          <Heading title="Joined mazes" />
          <div className="grid profile-map-grid">{c.map((x) => <ChainCard key={x.id} chain={x} />)}</div>
          {!c.length && <Empty text="No joined mazes yet." />}
        </>
      )}
      {own && portraitRuntime && (
        <div className="profile-avatar-runtime" aria-hidden="true">
          <RuntimeFrame
            pkg={portraitPackage}
            mode="home"
            onPackageApi={bindPortraitApi}
            onReady={capturePortrait}
          />
        </div>
      )}
    </main>
  );
}
function Auth() {
  const { user, login, loginWithGoogle, register, firebaseMode } = useAuth();
  const [newUser, setNew] = useState(false),
    [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [name, setName] = useState(""),
    [error, setError] = useState("");
  if (user) return <Navigate to="/" replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setError("");
      if (newUser) await register(email, password, name);
      else await login(email, password, name);
    } catch (x) {
      setError(x instanceof Error ? x.message : "Authentication failed");
    }
  };
  return (
    <main className="auth">
      <form onSubmit={submit}>
        <img className="auth-logo" src="/mattermaze.png" alt="MatterMaze" />
        {(newUser || !firebaseMode) && (
          <label>
            Display name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
        )}
        {firebaseMode && (
          <>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
              />
            </label>
          </>
        )}
        <button className="primary">
          {firebaseMode
            ? newUser
              ? "Register"
              : "Sign in"
            : "Continue locally"}
        </button>
        {firebaseMode && (
          <>
            <div className="auth-divider"><span>OR</span></div>
            <button
              type="button"
              className="google-auth-button"
              onClick={() => {
                setError("");
                void loginWithGoogle().catch((reason) =>
                  setError(reason instanceof Error ? reason.message : "Google sign-in failed"),
                );
              }}
            >
              <span aria-hidden="true">G</span>
              CONTINUE WITH GOOGLE
            </button>
          </>
        )}
        {firebaseMode && (
          <button
            type="button"
            className="linkbutton"
            onClick={() => setNew((v) => !v)}
          >
            {newUser
              ? "Already registered? Sign in"
              : "Need an account? Register"}
          </button>
        )}
        {error && <div className="error">{error}</div>}
      </form>
    </main>
  );
}
const EMPTY_HOARD_SUMMARY = {
  gold: 0,
  physicalGold: 0,
  totalGold: 0,
  completed: 0,
  rooms: 1,
  roomName: "Main Room",
  theme: "Depot",
  placed: 0,
  hero: "Dex Boy",
};

function summarizeHoardState(state: Record<string, unknown> | null) {
  if (!state) return EMPTY_HOARD_SUMMARY;
  const completed =
    state.completedMaps && typeof state.completedMaps === "object"
      ? Object.keys(state.completedMaps as Record<string, unknown>).length
      : 0;
  const rooms =
    state.homeRooms &&
    typeof state.homeRooms === "object" &&
    !Array.isArray(state.homeRooms)
      ? (state.homeRooms as Record<string, Record<string, unknown>>)
      : {};
  const activeId =
    typeof state.activeHomeRoom === "string"
      ? state.activeHomeRoom
      : "room_main";
  const room = rooms[activeId] || rooms.room_main || {};
  const placed = Array.isArray(state.homeDecor)
    ? state.homeDecor.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          (item as Record<string, unknown>).roomId === activeId,
      ).length
    : 0;
  const walletGold = Math.max(0, Math.floor(Number(state.gold) || 0));
  const settledPhysicalGold = Array.isArray(state.homeGoldPiles)
    ? state.homeGoldPiles.reduce((total, item) => {
        if (!item || typeof item !== "object") return total;
        const treasure = item as Record<string, unknown>;
        return total + Math.max(0, Math.floor(Number(treasure.value ?? treasure.amount) || 0));
      }, 0)
    : 0;
  const pendingPhysicalGold = state.pendingGoldRain && typeof state.pendingGoldRain === "object"
    ? Math.max(0, Math.floor(Number((state.pendingGoldRain as Record<string, unknown>).remaining) || 0))
    : 0;
  const physicalGold = settledPhysicalGold + pendingPhysicalGold;
  return {
    gold: walletGold,
    physicalGold,
    totalGold: walletGold + physicalGold,
    completed,
    rooms: Math.max(1, Object.keys(rooms).length),
    roomName: typeof room.name === "string" ? room.name : "Main Room",
    theme: typeof room.theme === "string" ? room.theme : "Depot",
    placed,
    hero: state.hero === "girl" ? "Dex Girl" : "Dex Boy",
  };
}

function TrophyBadge({ trophy, compact = false }: { trophy: TrophyResult; compact?: boolean }) {
  return (
    <span
      className={`trophy-badge trophy-${trophy.track} trophy-${trophy.tier}${compact ? " compact" : ""}`}
      title={`${trophy.name} · ${trophy.material}`}
      aria-hidden="true"
    >
      <Trophy size={compact ? 15 : 20} strokeWidth={2.6} />
      <i />
    </span>
  );
}

function mergeVisitorCharacter(
  hoardState: Record<string, unknown> | null,
  visitorState: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!hoardState) return null;
  const customization = visitorState?.characterCustomization;
  return {
    ...hoardState,
    hero: visitorState?.hero === "girl" ? "girl" : "boy",
    characterCustomization:
      customization && typeof customization === "object"
        ? customization
        : { owned: [], ownedColorways: [], equipped: {}, colorways: {} },
  };
}

function Hoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const ownerId = userId || user?.uid || "";
  const visiting = !!user && ownerId !== user.uid;
  const [summary, setSummary] = useState(EMPTY_HOARD_SUMMARY),
    [owner, setOwner] = useState<HubProfile | null>(null),
    [publicState, setPublicState] = useState<Record<string, unknown> | null>(null),
    [verifiedMaps, setVerifiedMaps] = useState<HubMap[]>([]),
    [achievements, setAchievements] = useState<TrophyResult[]>([]),
    [hoardLoading, setHoardLoading] = useState(true),
    [saveStatus, setSaveStatus] = useState<RuntimeSaveStatus>("loading"),
    [runtimeApi, setRuntimeApi] = useState<RuntimeFrameHandle | null>(null),
    [runtimeFullscreen, setRuntimeFullscreen] = useState(false),
    [share, setShare] = useState<SharePayload | null>(null),
    [sharing, setSharing] = useState(false);
  const homePackage = useMemo(() => blankMapPackage(), []);
  const updateSummary = useCallback(
    (state: Record<string, unknown>) => {
      const next = summarizeHoardState(state);
      setSummary((current) =>
        current.gold === next.gold &&
        current.physicalGold === next.physicalGold &&
        current.totalGold === next.totalGold &&
        current.completed === next.completed &&
        current.rooms === next.rooms &&
        current.roomName === next.roomName &&
        current.theme === next.theme &&
        current.placed === next.placed &&
        current.hero === next.hero
          ? current
          : next,
      );
    },
    [],
  );
  const updateRuntimeState = useCallback((state: Record<string, unknown>) => {
    // RuntimeFrame owns the live Hoard state. Mirroring every physics save back
    // into profileOverride caused the iframe to reload and cycle rooms.
    updateSummary(state);
  }, [updateSummary]);
  useEffect(() => {
    if (!ownerId) return;
    let active = true;
    setHoardLoading(true);
    setSaveStatus("loading");
    void Promise.all([
      repo.profile(ownerId),
      repo.maps({ authorId: ownerId }),
      visiting ? loadPublicHoard(ownerId) : loadPlayerState(ownerId),
      visiting && user ? loadPlayerState(user.uid) : Promise.resolve(null),
    ]).then(([profile, maps, state, visitorState]) => {
      if (!active) return;
      const nextAchievements = evaluateTrophies(maps);
      const earnedTrophies = nextAchievements.filter((item) => item.unlocked).map((item) => item.id);
      const previousEarned = Array.isArray(state?.earnedTrophies) ? state.earnedTrophies.filter((id): id is string => typeof id === "string") : [];
      const ownerState = state ? { ...state, earnedTrophies } : null;
      const nextState = visiting ? mergeVisitorCharacter(ownerState, visitorState) : ownerState;
      setOwner(profile);
      setVerifiedMaps(maps.filter((map) => map.verified).sort((a, b) => b.uniqueAttemptCount - a.uniqueAttemptCount));
      setAchievements(nextAchievements);
      setPublicState(nextState);
      if (ownerState) updateSummary(ownerState);
      if (!visiting && JSON.stringify(previousEarned.slice().sort()) !== JSON.stringify(earnedTrophies.slice().sort()))
        void savePlayerState(ownerId, nextState || {}).catch((error) => console.error("Trophy unlock save failed.", error));
    }).catch((error) => {
      console.warn("Hoard account data unavailable; opening the local room.", error);
      if (active) setPublicState(null);
    }).finally(() => {
      if (active) setHoardLoading(false);
    });
    if (visiting) void economy.recordHoardVisit(ownerId).catch(() => {});
    return () => { active = false; };
  }, [ownerId, updateSummary, visiting]);
  const trophyProgress = (["crowd", "escape", "lineage", "nightmare"] as const).map((track) => {
    const trackTrophies = achievements.filter((trophy) => trophy.track === track);
    return trackTrophies.find((trophy) => !trophy.unlocked) || trackTrophies.at(-1);
  }).filter((trophy): trophy is TrophyResult => !!trophy);
  const earnedTrophies = achievements.filter((trophy) => trophy.unlocked);
  const shareHoard = () => {
    if (sharing) return;
    const name = owner?.displayName || user?.displayName || "Creator";
    setShare({
      title: `${name}'s Hoard`,
      text: `Visit ${name}'s MatterMaze Hoard: ${summary.totalGold.toLocaleString()}G treasure, ${summary.completed} completed mazes and ${earnedTrophies.length} trophies.`,
      url: `${location.origin}/hoard/${encodeURIComponent(ownerId)}`,
      kicker: `${summary.totalGold.toLocaleString()}G · ${earnedTrophies.length} trophies`,
      filename: `mattermaze-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "hoard"}-hoard.png`,
      source: "hoard",
    });
    if (!runtimeApi) return;
    setSharing(true);
    void runtimeApi.requestScreenshot()
      .then((image) => setShare((current) => current ? { ...current, image } : current))
      .catch((error) => console.warn("Hoard share capture unavailable; sharing the direct link.", error))
      .finally(() => setSharing(false));
  };
  return (
    <main className="hoard-page">
      <aside className="hoard-rail hoard-left">
        <section className="pixel-panel player-panel">
          <small>PLAYER</small>
          <div className="pixel-avatar">{owner?.avatarUrl ? <img src={owner.avatarUrl} alt={`${owner.displayName} profile`} /> : <PixelGlyph name="profile" size={28} />}</div>
          <div><b>{owner?.displayName || user?.displayName || "CREATOR"}</b><span>{summary.hero}</span></div>
          <dl>
            <div><dt>COMPLETED MAZES</dt><dd>{summary.completed}</dd></div>
            <div><dt>ROOMS</dt><dd>{summary.rooms}</dd></div>
          </dl>
        </section>
        <section className="pixel-panel gold-panel">
          <small>{visiting ? "VISIBLE TREASURE" : "YOUR GOLD"}</small>
          <strong><Coins size={22} />{summary.totalGold.toLocaleString()}</strong>
          <span>{summary.gold.toLocaleString()}g wallet · {summary.physicalGold.toLocaleString()}g in Hoard</span>
        </section>
        {!visiting && <section className="pixel-panel quick-panel">
          <small>QUICK ACCESS</small>
          <Link to="/play"><PixelGlyph name="play" size={17} /> Play community mazes</Link>
          <Link to="/build"><PixelGlyph name="create" size={17} /> Create a maze</Link>
        </section>}
        <section className="pixel-panel hoard-milestones"><small>TROPHY PROGRESS</small>{trophyProgress.map((trophy) => <div className={trophy.unlocked ? "unlocked" : "locked"} key={trophy.id}><TrophyBadge trophy={trophy} compact/><span><b>{trophy.name.toUpperCase()}</b><small>{trophy.progress}</small></span></div>)}</section>
      </aside>
      <section className="hoard-stage game-mode-shell">
        <div className={`hoard-viewport-row${runtimeFullscreen ? " runtime-fullscreen" : ""}`}>
          <div className="shell-runtime hoard-runtime">
            {!hoardLoading && (!visiting || publicState) ? <RuntimeFrame
              pkg={homePackage}
              mode="home"
              onPackageApi={setRuntimeApi}
              onProfileChanged={visiting ? undefined : updateRuntimeState}
              onSaveStatus={visiting ? undefined : setSaveStatus}
              onFullscreenChange={setRuntimeFullscreen}
              profileOverride={publicState}
              readOnlyProfile={visiting}
              returnPath={visiting ? `/hoard/${ownerId}` : "/hoard"}
            /> : <Empty text={hoardLoading ? "Loading creator Hoard…" : "This creator has not opened their Hoard since public visits were enabled."} />}
          </div>
          {!visiting && !runtimeFullscreen && <nav className="hoard-viewport-actions" aria-label="Hoard tools">
            <button
              type="button"
              className="hoard-viewport-action manage"
              disabled={!runtimeApi}
              onClick={() => runtimeApi?.homeAction("furnish")}
              title="Hoard management"
            ><PixelGlyph name="manage" size={24} /><span>MANAGE</span></button>
            <button
              type="button"
              className="hoard-viewport-action edit"
              disabled={!runtimeApi}
              onClick={() => runtimeApi?.homeAction("edit")}
              title="Hoard editor"
            ><PixelGlyph name="hoardEdit" size={24} /><span>EDIT</span></button>
            <button
              type="button"
              className="hoard-viewport-action character"
              onClick={() => navigate("/character")}
              title="Character editor and shop"
            ><PixelGlyph name="wardrobe" size={24} /><span>CHARACTER</span></button>
          </nav>}
        </div>
      </section>
      <aside className="hoard-rail hoard-right">
        <section className="pixel-panel">
          <small>ROOM INFO</small>
          <h2>{summary.roomName}</h2>
          <dl>
            <div><dt>THEME</dt><dd>{summary.theme.toUpperCase()}</dd></div>
            <div><dt>SIZE</dt><dd>20 × 30</dd></div>
            <div><dt>PLACED ITEMS</dt><dd>{summary.placed}</dd></div>
            <div><dt>SAVE</dt><dd className={`live-value save-${saveStatus}`}>{saveStatus === "loading" ? "LOADING" : saveStatus === "saving" ? "SAVING…" : saveStatus === "error" ? "SAVE FAILED" : "SYNCED"}</dd></div>
          </dl>
          <button type="button" className="hoard-share-button" disabled={sharing} onClick={shareHoard}><Share2 size={16} /> {sharing ? "CAPTURING…" : "SHARE HOARD"}</button>
        </section>
        <section className="pixel-panel hoard-trophies">
          <small>TROPHY CABINET</small>
          {earnedTrophies.map((trophy) => <article className={`trophy-cabinet-item trophy-${trophy.tier}`} key={trophy.id}><TrophyBadge trophy={trophy}/><span><b>{trophy.name.toUpperCase()}</b><small>{trophy.material} · placeable in Furnish</small></span></article>)}
          {!earnedTrophies.length && <p>Achievement trophies appear here and in Furnish as soon as their target is reached.</p>}
        </section>
        <section className="pixel-panel hoard-trophies maze-records">
          <small>VERIFIED MAZE RECORDS</small>
          {verifiedMaps.slice(0, 3).map((map) => <Link key={map.id} to={`/map/${map.id}`}><b>{map.title}</b><span>{map.uniqueAttemptCount.toLocaleString()} attempts · {map.uniqueAttemptCount ? (map.escapeCount / map.uniqueAttemptCount * 100).toFixed(1) : "0.0"}% escaped</span><em>BOUNTY {map.bountyGold}G</em></Link>)}
          {!verifiedMaps.length && <p>No verified maze records yet.</p>}
        </section>
      </aside>
      <ShareDialog payload={share} onClose={() => setShare(null)} />
    </main>
  );
}
function Character() {
  return (
    <main className="shell-destination">
      <div className="destination-head">
        <div>
          <small>IDENTITY · OWNED / SHOP / EQUIPPED</small>
          <h1>Character</h1>
          <p>Preview, purchase and equip character parts for every maze.</p>
        </div>
        <Link className="secondary" to="/hoard">
          Return to Hoard
        </Link>
      </div>
      <div className="shell-runtime character-runtime">
        <RuntimeFrame pkg={blankMapPackage()} mode="character" />
      </div>
    </main>
  );
}
function PlayMapCard({ map }: { map: HubMap }) {
  return (
    <article className="play-cover-card">
      <Link
        className={`play-cover-art ${map.theme}`}
        style={map.thumbnailUrl ? { backgroundImage: `url(${map.thumbnailUrl})` } : undefined}
        to={`/play/${map.id}`}
      >
        {!map.thumbnailUrl && <span>{map.theme.toUpperCase()}</span>}
        <em>{map.levelType}</em>
        {map.verified && <strong className="play-bounty"><Coins size={13} />{map.bountyGold}G</strong>}
      </Link>
      <div className="play-cover-body">
        <Link className="title" to={`/play/${map.id}`}>{map.title}</Link>
        <Link className="author" to={`/profile/${map.authorId}`}>by {map.authorName}</Link>
        <div className="play-facts">
          <span><Star size={13} fill="currentColor" />{map.ratingCount ? map.averageRating.toFixed(1) : "NEW"}</span>
          <span><Gamepad2 size={13} />{map.playCount.toLocaleString()} plays</span>
          <span>{map.theme}</span>
          <span>{map.uniqueAttemptCount ? `${(map.escapeCount / map.uniqueAttemptCount * 100).toFixed(1)}% clear` : "Unchallenged"}</span>
        </div>
        <Link className="play-launch" to={`/play/${map.id}`}><Gamepad2 size={17} /> PLAY</Link>
      </div>
    </article>
  );
}

function PlayWorldCard({ chain }: { chain: HubChain }) {
  return (
    <article className="play-world-card">
      <div className="play-world-sequence">
        {chain.segments.slice(0, 4).map((segment, index) => (
          <span key={`${segment.mapId}-${index}`} style={segment.thumbnailUrl ? { backgroundImage: `url(${segment.thumbnailUrl})` } : undefined}>{index + 1}</span>
        ))}
      </div>
      <div>
        <small>JOINED MAZE · {chain.segments.length} MAZES</small>
        <Link className="title" to={`/chain/${chain.id}`}>{chain.title}</Link>
        <Link className="author" to={`/profile/${chain.authorId}`}>by {chain.authorName}</Link>
        <p>{chain.description || "A connected sequence of mazes."}</p>
        <Link className="play-launch" to={`/world/${chain.id}`}><Gamepad2 size={17} /> START JOINED MAZE</Link>
      </div>
    </article>
  );
}

function PlayHub() {
  const nav = useNavigate(),
    [maps, setMaps] = useState<HubMap[]>([]),
    [chains, setChains] = useState<HubChain[]>([]),
    [q, setQ] = useState(""),
    [sort, setSort] = useState<"rating" | "newest" | "played">("rating"),
    [tab, setTab] = useState<"all" | "maps" | "worlds">("all"),
    [theme, setTheme] = useState("all");
  useEffect(() => {
    void Promise.all([
      repo.maps({ publicOnly: true }),
      repo.chains({ publicOnly: true }),
    ]).then(([m, c]) => {
      setMaps(m);
      setChains(c);
    });
  }, []);
  const term = q.trim().toLowerCase(),
    shown = maps
      .filter((m) =>
        (theme === "all" || m.theme === theme) &&
        [m.title, m.authorName, m.theme, ...m.tags]
          .join(" ")
          .toLowerCase()
          .includes(term),
      )
      .sort((a, b) =>
        sort === "newest"
          ? b.createdAt.getTime() - a.createdAt.getTime()
          : sort === "played"
            ? b.playCount - a.playCount || b.averageRating - a.averageRating
            : b.averageRating - a.averageRating ||
              b.ratingCount - a.ratingCount ||
              b.playCount - a.playCount,
      ),
    shownChains = chains.filter((chain) =>
      [chain.title, chain.authorName, chain.description, ...chain.segments.map((segment) => segment.title)]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  return (
    <main className="page shell-page play-page play-menu-page">
      <div className="page-title">
        <div>
          <small>CHOOSE · LAUNCH · COMPLETE</small>
          <h1>Play</h1>
          <p>Choose a maze, chase its bounty and make your escape.</p>
        </div>
        <label className="search">
          <Search size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by maze, creator or theme…" />
        </label>
      </div>
      <section className="play-quick-strip single">
        <button disabled={!shown.length} onClick={() => shown.length && nav(`/play/${shown[Math.floor(Math.random() * shown.length)].id}`)}><Compass size={20} /><span><b>RANDOM MAZE</b><small>Jump into a challenge</small></span></button>
      </section>
      <nav className="section-menu play-type-tabs" aria-label="Filter playable mazes">
        <button className={tab === "all" ? "active" : ""} onClick={() => setTab("all")}>All mazes <b>{shown.length + shownChains.length}</b></button>
        <button className={tab === "maps" ? "active" : ""} onClick={() => setTab("maps")}>Standalone <b>{shown.length}</b></button>
        <button className={tab === "worlds" ? "active" : ""} onClick={() => setTab("worlds")}>Joined <b>{shownChains.length}</b></button>
      </nav>
      <details className="play-filter-drawer">
        <summary>BROWSE FILTERS <span>{tab === "worlds" ? `JOINED · ${shownChains.length}` : `${sort.toUpperCase()} · ${theme === "all" ? "ALL THEMES" : theme.toUpperCase()}`}</span></summary>
        <div className="play-layout">
        <aside className="play-browser-tools">
          {tab !== "worlds" && <section className="pixel-panel">
            <small>SORT GAMES</small>
            <button className={sort === "rating" ? "active" : ""} onClick={() => setSort("rating")}><Star size={13} /> Top rated</button>
            <button className={sort === "newest" ? "active" : ""} onClick={() => setSort("newest")}><Calendar size={13} /> Newest</button>
            <button className={sort === "played" ? "active" : ""} onClick={() => setSort("played")}><Gamepad2 size={13} /> Most played</button>
          </section>}
          {tab !== "worlds" && <section className="pixel-panel">
            <small>THEME</small>
            {["all", ...new Set(maps.map((map) => map.theme))].map((value) => <button key={value} className={theme === value ? "active" : ""} onClick={() => setTheme(value)}>{value === "all" ? "All themes" : value}</button>)}
          </section>}
        </aside>
        </div>
      </details>
      <section className="play-catalogue">
        {tab !== "worlds" && <><Heading title={`Playable mazes · ${shown.length}`} />{shown.length ? <div className="play-cover-grid">{shown.map((map) => <PlayMapCard key={map.id} map={map} />)}</div> : <Empty text="No playable mazes match these filters." />}</>}
        {tab !== "maps" && <div className="play-joined-feed"><Heading title={`Joined mazes · ${shownChains.length}`} />{shownChains.length ? <div className="play-world-grid">{shownChains.map((chain) => <PlayWorldCard key={chain.id} chain={chain} />)}</div> : <Empty text="No joined mazes match these filters." />}</div>}
      </section>
    </main>
  );
}
function CommunityHub() {
  return <Feed />;
}

type TutorialTab = "controls" | "building" | "hoard" | "character" | "community";

const Key = ({ children }: { children: ReactNode }) => <kbd className="tutorial-key">{children}</kbd>;

function Tutorial() {
  const [tab, setTab] = useState<TutorialTab>("controls");
  const [stylesReady, setStylesReady] = useState(false);
  useEffect(() => {
    let active = true;
    void import("./tutorial.css").then(() => { if (active) setStylesReady(true); });
    return () => { active = false; };
  }, []);
  const tabs: Array<{ id: TutorialTab; number: string; label: string; note: string; glyph: PixelGlyphName }> = [
    { id: "controls", number: "01", label: "Game controls", note: "PC + mobile", glyph: "play" },
    { id: "building", number: "02", label: "Building", note: "Create + publish", glyph: "create" },
    { id: "hoard", number: "03", label: "Hoard", note: "Rooms + economy", glyph: "hoard" },
    { id: "character", number: "04", label: "Character", note: "Looks + wardrobe", glyph: "profile" },
    { id: "community", number: "05", label: "Community", note: "People + sharing", glyph: "community" },
  ];
  if (!stylesReady) return <main className="page"><div className="empty">Opening the MatterMaze field manual…</div></main>;
  return (
    <main className="page tutorial-page">
      <section className="tutorial-hero">
        <div>
          <small>FIELD MANUAL</small>
          <h1>How to play MatterMaze</h1>
          <p>Controls, construction, Hoard management, character looks and community tools—organized around what you are trying to do.</p>
        </div>
        <PixelGlyph name="help" size={54} />
      </section>
      <div className="tutorial-tabs" role="tablist" aria-label="MatterMaze guide sections">
        {tabs.map((item) => <button key={item.id} type="button" role="tab" data-tone={item.id} aria-selected={tab === item.id} className={tab === item.id ? "active" : ""} onClick={() => setTab(item.id)}>
          <span>{item.number}</span><PixelGlyph name={item.glyph} size={20} /><b>{item.label}</b><small>{item.note}</small>
        </button>)}
      </div>

      {tab === "controls" && <section className="tutorial-section tutorial-tone-controls" role="tabpanel">
        <header><Gamepad2 size={25} /><div><small>GAME CONTROLS</small><h2>One control set, two ways to play</h2></div></header>
        <div className="tutorial-two-column">
          <article className="tutorial-card">
            <div className="tutorial-card-title"><span className="tutorial-platform-icon">PC</span><div><h3>Keyboard</h3><p>Arrow keys or familiar letter controls.</p></div></div>
            <div className="control-list">
              <div><span><Key>A</Key><Key>←</Key></span><p><b>Move left</b>Walk or steer while climbing.</p></div>
              <div><span><Key>D</Key><Key>→</Key></span><p><b>Move right</b>Walk or steer while climbing.</p></div>
              <div><span><Key>W</Key><Key>↑</Key><Key>SPACE</Key></span><p><b>Jump / climb up</b>Jump on the ground; climb when aligned with a ladder.</p></div>
              <div><span><Key>S</Key><Key>↓</Key></span><p><b>Climb down</b>Descend ladders and drop through one-way platforms.</p></div>
              <div><span><Key>E</Key></span><p><b>Interact</b>Use switches, doors, furniture and nearby objects.</p></div>
              <div><span><Key>1</Key><Key>2</Key><Key>3</Key></span><p><b>Select tool</b>Shovel, pickaxe or bucket. <Key>Q</Key> cycles them.</p></div>
              <div><span><Key>F</Key></span><p><b>Use selected tool</b>Hold to dig, mine or collect nearby matter.</p></div>
              <div className="charge-control"><span><Key>R</Key><Key>X</Key></span><p><b>Charge and spill</b>Hold, then release. A quick tap drops nearby; 2.5 seconds reaches the five-block maximum.</p></div>
            </div>
          </article>
          <article className="tutorial-card">
            <div className="tutorial-card-title"><span className="tutorial-platform-icon phone">▯</span><div><h3>Mobile control deck</h3><p>The artwork is the controller; the touch areas sit directly over its buttons.</p></div></div>
            <div className="mobile-control-demo" aria-label="Mobile controller layout illustration">
              <section><small>MOVE</small><div className="mobile-demo-row"><i>←</i><i>↓</i><i>→</i></div><p>Move and climb down</p></section>
              <section><small>TOOLS</small><div className="mobile-demo-tools"><i>SH</i><i>PK</i><i>BU</i><i>SP</i></div><p>Shovel · pickaxe · bucket · spill</p></section>
              <section><small>ACTION</small><div className="mobile-demo-row two"><i>✋</i><i>↑</i></div><p>Interact · jump / climb</p></section>
            </div>
            <div className="tutorial-mini-list"><p><b>Sound</b> toggles game audio.</p><p><b>Full screen</b> expands the game cabinet and hides the site shell.</p><p><b>Tool indicators</b> light while their matching button is held.</p></div>
          </article>
        </div>
        <div className="tutorial-actions"><Link className="primary" to="/play"><Gamepad2 size={16} /> CHOOSE A MAZE</Link></div>
      </section>}

      {tab === "building" && <section className="tutorial-section tutorial-tone-building" role="tabpanel">
        <header><PixelGlyph name="create" size={27} /><div><small>BUILDING KNOWLEDGE</small><h2>From an idea to a verified maze</h2></div></header>
        <div className="tutorial-workflow">
          <article><b>1</b><div><h3>Start or remix</h3><p>Create a blank draft, or choose <strong>Remix in editor</strong> on a published maze. A remix is your own editable revision while keeping permanent credit to its ancestors.</p></div></article>
          <article><b>2</b><div><h3>Build the space</h3><p>Open <strong>Assets</strong> for terrain, materials, interactive devices and markers. Asset theme and background are separate, so scenery can be mixed without changing the device set.</p></div></article>
          <article><b>3</b><div><h3>Draw and undo</h3><p>Open <strong>Tools</strong> for Pen, Erase, Line, Rectangle, Circle and Logic. Brush size, outline/fill, Undo, Redo and Clear stay in the same tool palette.</p></div></article>
          <article><b>4</b><div><h3>Wire devices</h3><p>Use Logic to select a trigger and connect its actions. Simple colored channels are quick shared circuits; advanced sequences can target devices in order and include timing or repeated behavior.</p></div></article>
          <article><b>5</b><div><h3>Prepare the solver kit</h3><p>Select up to ten devices players may place before a run. The solver only sees those chosen items and cannot edit the rest of the maze.</p></div></article>
          <article><b>6</b><div><h3>Run to validate</h3><p>Choose <strong>Run</strong> and complete the exact saved revision yourself: collect the gold cache and reach the exit. Editing afterward creates a new revision that must be validated again.</p></div></article>
          <article><b>7</b><div><h3>Save and publish</h3><p><strong>Save Draft</strong> keeps private work. <strong>Publish</strong> becomes available for the creator-completed revision and adds its playable card to the community.</p></div></article>
        </div>
        <div className="tutorial-note-grid"><div><ShieldCheck size={22} /><p><b>Verification belongs to a revision.</b> It proves the published layout has a chest and exit that its creator completed.</p></div><div><GitFork size={22} /><p><b>Remixes keep lineage.</b> Credit and creator earnings continue through the entire ancestor chain.</p></div></div>
        <div className="tutorial-actions"><Link className="primary" to="/build"><PixelGlyph name="create" size={16} /> OPEN CREATOR</Link></div>
      </section>}

      {tab === "hoard" && <section className="tutorial-section tutorial-tone-hoard" role="tabpanel">
        <header><PixelGlyph name="hoard" size={27} /><div><small>HOARD FIELD GUIDE</small><h2>Rooms, furniture and real treasure</h2></div></header>
        <div className="tutorial-three-column">
          <article className="tutorial-card"><h3>Rooms</h3><p>Your Main Room is permanent. In Manage mode you can add, duplicate, rename, theme and remove additional rooms, then use the left/right room controls while visiting.</p></article>
          <article className="tutorial-card"><h3>Room Doors</h3><p>Place Room Doors in the Hoard editor. Select a door in Logic, choose its destination room and destination door, then pair it. Doors create the physical route through your Hoard.</p></article>
          <article className="tutorial-card"><h3>Furniture and menus</h3><p><b>Workbench</b> opens the Hoard editor. <b>Wardrobe Locker</b> opens character customization and its shop. <b>Vault Chest</b> opens physical treasure controls. Other furniture can be bought, stored, placed and interacted with.</p></article>
        </div>
        <section className="economy-guide">
          <header><Coins size={23} /><div><small>GOLD ECONOMY</small><h3>How gold moves through MatterMaze</h3></div></header>
          <div className="economy-flow">
            <article><span>PLAY</span><b>Complete verified mazes</b><p>Every verified revision starts with at least a 25G bounty. Unique player failures make difficult mazes worth more. You can claim a revision’s bounty once; creators cannot claim their own.</p></article>
            <i>→</i>
            <article><span>POCKET</span><b>Wallet gold</b><p>Bounties and creator earnings arrive as spendable pocket gold. It follows your account and pays for character looks, furniture and other purchases.</p></article>
            <i>⇄</i>
            <article><span>HOARD</span><b>Physical treasury</b><p>Use the Vault Chest to dump wallet gold into a room as coins, bars, gems and chalices. It becomes visible, physical treasure you can scoop, carry and pour elsewhere—or collect back into your wallet.</p></article>
          </div>
          <div className="economy-rules">
            <div><Trophy size={20} /><p><b>Making mazes</b>Publishing alone does not print gold. Meaningful play by other people rewards the creator; difficulty, healthy engagement, saves, likes and rematches influence its value.</p></div>
            <div><GitFork size={20} /><p><b>Remix royalties</b>For a remix with ancestors, the current creator receives half of each creator reward. The other half is divided across every maze in its permanent lineage. An original with no ancestors keeps all of its creator reward.</p></div>
            <div><Briefcase size={20} /><p><b>Pocket versus pile</b>Pocket gold is the convenient balance shown in the header. Physical Hoard gold still belongs to you and can fund Hoard furniture; collect it back to the wallet when you want it available everywhere.</p></div>
          </div>
        </section>
        <div className="tutorial-actions"><Link className="primary" to="/hoard"><PixelGlyph name="hoard" size={16} /> ENTER HOARD</Link></div>
      </section>}

      {tab === "character" && <section className="tutorial-section tutorial-tone-character" role="tabpanel">
        <header><PixelGlyph name="profile" size={27} /><div><small>CHARACTER CUSTOMIZATION</small><h2>Build a look that follows your account</h2></div></header>
        <div className="tutorial-workflow compact">
          <article><b>1</b><div><h3>Open the character workshop</h3><p>Use the Character button beside your Hoard, choose Character from the site, or interact with the Wardrobe Locker inside your room.</p></div></article>
          <article><b>2</b><div><h3>Choose a body and equipment</h3><p>Browse the available body shapes, outfits and equipment slots. Preview changes on Dex before committing to the look.</p></div></article>
          <article><b>3</b><div><h3>Own and equip styles</h3><p>Starter items are available immediately. Purchased looks and colorways stay owned, while the equipped selection becomes your in-game character across mazes and Hoard rooms.</p></div></article>
          <article><b>4</b><div><h3>Keep profile identity separate</h3><p>Your creator-page portrait can be an uploaded photo or a Character Snap. Changing that portrait does not change the character sprite you play.</p></div></article>
        </div>
        <div className="tutorial-actions"><Link className="primary" to="/character"><PixelGlyph name="profile" size={16} /> CUSTOMIZE CHARACTER</Link></div>
      </section>}

      {tab === "community" && <section className="tutorial-section tutorial-tone-community" role="tabpanel">
        <header><PixelGlyph name="community" size={27} /><div><small>COMMUNITY FUNCTIONS</small><h2>Find creators, mazes and conversations</h2></div></header>
        <div className="tutorial-three-column community-guide-grid">
          <article className="tutorial-card"><Search size={22} /><h3>Discover</h3><p>Search maze titles, descriptions and creator names. Switch between Feed, Following, Trending, Creators and your own publications.</p></article>
          <article className="tutorial-card"><Users size={22} /><h3>Creator pages</h3><p>Open a creator’s page to see their mazes, Joined Mazes, profile information and following list. Follow people to build your Following feed.</p></article>
          <article className="tutorial-card"><MessageSquare size={22} /><h3>React and discuss</h3><p>Like and comment on publications, save mazes for later, rate completed experiences and use the community signals to find well-loved work.</p></article>
          <article className="tutorial-card"><Share2 size={22} /><h3>Share</h3><p>Share maze pages, victories and Hoards. On supported phones the system share sheet opens; copy-link and image options provide fallbacks.</p></article>
          <article className="tutorial-card"><GitFork size={22} /><h3>Remix</h3><p>Open a public maze and choose Remix in editor. Your publication remains connected to the source and all older ancestors for attribution and royalties.</p></article>
          <article className="tutorial-card"><Link2 size={22} /><h3>Joined Mazes</h3><p>Join published mazes into an ordered multi-maze run. Players progress through the sequence in order rather than freely switching rooms.</p></article>
        </div>
        <div className="tutorial-actions"><Link className="primary" to="/community"><PixelGlyph name="community" size={16} /> OPEN COMMUNITY</Link></div>
      </section>}
    </main>
  );
}

function BuildHub() {
  const nav = useNavigate();
  useEffect(() => {
    let active = true;
    void listProjects().then(async (projects) => {
      const project = projects[0] || await createProject(blankMapPackage());
      if (active) nav(`/build/${project.id}`, { replace: true });
    });
    return () => { active = false; };
  }, [nav]);
  return <main className="page"><Empty text="Opening your latest draft…" /></main>;
}
function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading)
    return <div className="app-loading">Loading MatterMaze…</div>;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}
function AdminOnly({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth();
  return isAdmin ? <>{children}</> : <Navigate to="/" replace />;
}
function Admin() {
  const [users, setUsers] = useState<HubProfile[]>([]),
    [posts, setPosts] = useState<Awaited<ReturnType<typeof repo.adminPosts>>>(
      [],
    ),
    [tab, setTab] = useState<"users" | "posts">("users"),
    [busy, setBusy] = useState("");
  const refresh = () =>
    void Promise.all([repo.profiles(true), repo.adminPosts()]).then(
      ([u, p]) => {
        setUsers(u);
        setPosts(p);
      },
    );
  useEffect(refresh, []);
  const suspend = async (p: HubProfile) => {
    setBusy(p.id);
    await repo.adminSetUserSuspended(p.id, !p.isSuspended);
    refresh();
    setBusy("");
  };
  const moderate = async (p: (typeof posts)[number]) => {
    setBusy(p.id);
    await repo.adminSetPostRemoved(p.type, p.id, !p.isRemoved);
    refresh();
    setBusy("");
  };
  return (
    <main className="page">
      <div className="page-title">
        <div>
          <small>ADMINISTRATION</small>
          <h1>Community management</h1>
          <p>
            Manage creator access and archive or restore community posts without
            destroying their lineage.
          </p>
        </div>
      </div>
      <div className="tabs">
        <button
          className={tab === "users" ? "active" : ""}
          onClick={() => setTab("users")}
        >
          <Users size={14} />
          Users · {users.length}
        </button>
        <button
          className={tab === "posts" ? "active" : ""}
          onClick={() => setTab("posts")}
        >
          <ShieldCheck size={14} />
          Posts · {posts.length}
        </button>
        <Link className="secondary" to="/studio">
          <Boxes size={14} />
          Asset Studio
        </Link>
      </div>
      <section className="admin-list">
        {tab === "users"
          ? users.map((p) => (
              <article key={p.id}>
                <div>
                  <b>{p.displayName}</b>
                  <small>{p.id}</small>
                </div>
                <span
                  className={
                    p.isSuspended ? "admin-status removed" : "admin-status"
                  }
                >
                  {p.isSuspended ? "Suspended" : "Active"}
                </span>
                <button
                  className="secondary"
                  disabled={busy === p.id}
                  onClick={() => void suspend(p)}
                >
                  {p.isSuspended ? "Restore user" : "Suspend user"}
                </button>
              </article>
            ))
          : posts.map((p) => (
              <article key={`${p.type}-${p.id}`}>
                <div>
                  <b>{p.title}</b>
                  <small>
                    {p.type === "map" ? "maze" : "joined maze"} · {p.authorName}
                  </small>
                </div>
                <span
                  className={
                    p.isRemoved ? "admin-status removed" : "admin-status"
                  }
                >
                  {p.isRemoved ? "Archived" : "Published"}
                </span>
                <Link
                  className="secondary"
                  to={p.type === "map" ? `/map/${p.id}` : `/chain/${p.id}`}
                >
                  Open
                </Link>
                <button
                  className="secondary"
                  disabled={busy === p.id}
                  onClick={() => void moderate(p)}
                >
                  {p.isRemoved ? "Restore" : "Archive"}
                </button>
              </article>
            ))}
      </section>
    </main>
  );
}
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Auth />} />
      <Route
        path="/*"
        element={
          <Protected>
            <Header />
            <Routes>
              <Route path="/" element={<Navigate to="/play" replace />} />
              <Route path="/hoard" element={<Hoard />} />
              <Route path="/hoard/:userId" element={<Hoard />} />
              <Route path="/play" element={<PlayHub />} />
              <Route path="/community" element={<CommunityHub />} />
              <Route path="/help" element={<Tutorial />} />
              <Route path="/bugs" element={<BugBoardRoute />} />
              <Route path="/bugs/:reportId" element={<BugBoardRoute />} />
              <Route path="/build" element={<BuildHub />} />
              <Route path="/build/:projectId" element={<EditProjectRoute />} />
              <Route path="/character" element={<Character />} />
              <Route
                path="/discover"
                element={<Navigate to="/play" replace />}
              />
              <Route path="/publish" element={<Publish />} />
              <Route path="/map/:mapId" element={<MapDetail />} />
              <Route path="/play/:mapId" element={<PlayMapRoute />} />
              <Route path="/create" element={<EditProjectRoute />} />
              <Route path="/create/:projectId" element={<EditProjectRoute />} />
              <Route
                path="/studio"
                element={
                  <AdminOnly>
                    <StudioRoute />
                  </AdminOnly>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminOnly>
                    <Admin />
                  </AdminOnly>
                }
              />
              <Route path="/join" element={<Join />} />
              <Route path="/chain/:chainId" element={<ChainDetail />} />
              <Route path="/world/:chainId" element={<JoinedWorldRoute />} />
              <Route path="/profile/:userId" element={<Profile />} />
              <Route path="*" element={<Navigate to="/play" replace />} />
            </Routes>
          </Protected>
        }
      />
    </Routes>
  );
}
